import * as React from "react";

import type { ActivityContentKpiEntryLogged } from "@/api";
import type { Activity } from "@/models/activities";
import type { ActivityHandler, FeedItemProps } from "../interfaces";
import { formatKpiValue as formatValue } from "turboui";

import { Trans } from "turboui";
import i18n from "@/i18n";
import { activityAuthorName, spaceLink } from "../feedItemLinks";

const KpiEntryLogged: ActivityHandler = {
  pageHtmlTitle(_activity: Activity) {
    throw new Error("Not implemented");
  },

  pagePath(paths, activity: Activity) {
    const data = content(activity);
    const spaceId = data.space?.id;
    const kpiId = data.kpi?.id;

    if (!spaceId) return paths.homePath();
    return kpiId ? paths.spaceKpiPath(spaceId, kpiId) : paths.spaceKpisPath(spaceId);
  },

  PageTitle(_props: { activity: Activity }) {
    throw new Error("Not implemented");
  },

  PageContent(_props: { activity: Activity }) {
    throw new Error("Not implemented");
  },

  PageOptions(_props: { activity: Activity }) {
    return null;
  },

  FeedItemTitle({ activity, page, paths }: FeedItemProps) {
    const data = content(activity);

    if (page === "space" || !data.space) {
      return <Trans i18nKey="{{author}} recorded a KPI value" values={{ author: activityAuthorName(activity) }} />;
    }

    return (
      <Trans
        i18nKey="{{author}} recorded a KPI value in the <space>{{spaceName}}</space> space"
        values={{ author: activityAuthorName(activity), spaceName: data.space.name }}
        components={{ space: spaceLink(paths, data.space) }}
      />
    );
  },

  FeedItemContent({ activity }: { activity: Activity }) {
    const data = content(activity);
    const kpiName = data.kpi?.name;
    if (kpiName == null)
      return (
        <Trans
          i18nKey="KPI: {{value}}"
          values={{ value: formatValue(data.value, data.kpi?.unit, i18n.resolvedLanguage) }}
        />
      );

    return (
      <>
        {kpiName}: {formatValue(data.value, data.kpi?.unit, i18n.resolvedLanguage)}
      </>
    );
  },

  feedItemAlignment(_activity: Activity): "items-start" | "items-center" {
    return "items-start";
  },

  commentCount(_activity: Activity): number {
    throw new Error("Not implemented");
  },

  hasComments(_activity: Activity): boolean {
    throw new Error("Not implemented");
  },

  NotificationTitle({ activity }: { activity: Activity }) {
    const data = content(activity);
    const kpiName = data.kpi?.name;
    const values = { kpiName, value: formatValue(data.value, data.kpi?.unit, i18n.resolvedLanguage) };
    return kpiName == null
      ? i18n.t("Recorded KPI value: {{value}}", values)
      : i18n.t("Recorded {{value}} for {{kpiName}}", values);
  },

  NotificationLocation({ activity }: { activity: Activity }) {
    return content(activity).space?.name ?? null;
  },
};

function content(activity: Activity): ActivityContentKpiEntryLogged {
  return activity.content as ActivityContentKpiEntryLogged;
}

export default KpiEntryLogged;
